// src/components/admin/RolesPermissionsTab.jsx
// Phase 0.3 — RBAC : rôles, permissions, attributions, effectives.
// Écritures réservées Super Admin côté Worker (UI masque seulement).

import React, { useCallback, useEffect, useState } from 'react';
import {
  ShieldCheck,
  Plus,
  X,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  ShieldAlert,
  CheckCircle2,
  KeyRound,
  UserCog,
} from 'lucide-react';
import * as rbac from '../../services/adminRolesPermissions';
import { authService } from '../../services/authService';

function isSuperAdminUser() {
  const user = authService.getCurrentUser();
  if (!user) return false;
  const n = user.niveau;
  if (n === 1 || n === '1') return true;
  if (typeof n === 'string' && n.toUpperCase() === 'SUPER_ADMIN') return true;
  return false;
}

function userErrorMessage(err) {
  const raw =
    (typeof err?.body?.error === 'string' && err.body.error) ||
    (typeof err?.body?.erreur === 'string' && err.body.erreur) ||
    err?.message ||
    '';
  const key = String(raw).trim();
  if (err?.status === 401 || /non authentifié/i.test(key)) {
    return 'Session expirée. Veuillez vous reconnecter.';
  }
  if (err?.status === 403 || /accès refusé/i.test(key)) {
    return 'Vous n’avez pas les droits nécessaires.';
  }
  if (err?.status === 404) return key || 'Ressource introuvable.';
  if (err?.status === 409) return key || 'Cette ressource existe déjà.';
  if (err?.status === 400) return key || 'Données invalides.';
  return key || 'Une erreur est survenue.';
}

function formatDate(value) {
  if (value === undefined || value === null || value === '') return null;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString('fr-FR');
  } catch {
    return String(value);
  }
}

/** datetime-local → ISO-ish string acceptable par le Worker */
function toApiDateExpiration(localValue) {
  if (!localValue) return '';
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return localValue;
  return d.toISOString();
}

export default function RolesPermissionsTab({ admins = [] }) {
  const superAdmin = isSuperAdminUser();

  const [subTab, setSubTab] = useState('roles'); // roles | permissions | attributions

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [busy, setBusy] = useState(false);

  const [expandedRoleId, setExpandedRoleId] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});

  const [adminRoles, setAdminRoles] = useState({});
  const [adminEffectives, setAdminEffectives] = useState({});
  const [openAdminId, setOpenAdminId] = useState(null);

  // Forms
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [roleForm, setRoleForm] = useState({ code: '', libelle: '', description: '' });
  const [showCreatePerm, setShowCreatePerm] = useState(false);
  const [permForm, setPermForm] = useState({ code: '', libelle: '', domaine: '' });

  // Confirm / action panels
  const [confirm, setConfirm] = useState(null); // { type, ...payload, message }
  const [tempDate, setTempDate] = useState('');
  const [assignRoleId, setAssignRoleId] = useState('');
  const [attachPermId, setAttachPermId] = useState('');

  const flash = (msg) => {
    setSuccess(msg);
    setError(null);
  };
  const fail = (err) => {
    setError(userErrorMessage(err));
    setSuccess(null);
  };

  const fetchRoles = useCallback(async () => {
    setLoadingRoles(true);
    try {
      const res = await rbac.getRoles();
      setRoles(Array.isArray(res?.roles) ? res.roles : []);
    } catch (err) {
      fail(err);
      setRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    setLoadingPerms(true);
    try {
      const res = await rbac.getPermissions();
      setPermissions(Array.isArray(res?.permissions) ? res.permissions : []);
    } catch (err) {
      fail(err);
      setPermissions([]);
    } finally {
      setLoadingPerms(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    if (subTab === 'permissions') fetchPermissions();
  }, [subTab, fetchPermissions]);

  const loadRolePerms = async (roleId, force = false) => {
    if (!force && rolePermissions[roleId]) return;
    try {
      const res = await rbac.getPermissionsDuRole(roleId);
      setRolePermissions((prev) => ({
        ...prev,
        [roleId]: Array.isArray(res?.permissions) ? res.permissions : [],
      }));
    } catch (err) {
      fail(err);
    }
  };

  const toggleRole = async (roleId) => {
    if (expandedRoleId === roleId) {
      setExpandedRoleId(null);
      return;
    }
    setExpandedRoleId(roleId);
    await loadRolePerms(roleId);
  };

  const fetchAdminRoles = async (adminId) => {
    setAdminRoles((prev) => ({ ...prev, [adminId]: 'loading' }));
    try {
      const res = await rbac.getRolesAdministrateur(adminId);
      setAdminRoles((prev) => ({
        ...prev,
        [adminId]: Array.isArray(res?.roles) ? res.roles : [],
      }));
    } catch (err) {
      fail(err);
      setAdminRoles((prev) => ({ ...prev, [adminId]: [] }));
    }
  };

  const fetchEffectives = async (adminId) => {
    setAdminEffectives((prev) => ({ ...prev, [adminId]: 'loading' }));
    try {
      const res = await rbac.getPermissionsEffectives(adminId);
      setAdminEffectives((prev) => ({
        ...prev,
        [adminId]: Array.isArray(res?.permissions) ? res.permissions : [],
      }));
    } catch (err) {
      fail(err);
      setAdminEffectives((prev) => ({ ...prev, [adminId]: [] }));
    }
  };

  const openAdminPanel = (adminId) => {
    const next = openAdminId === adminId ? null : adminId;
    setOpenAdminId(next);
    if (next != null) {
      fetchAdminRoles(next);
      fetchEffectives(next);
    }
  };

  /* ---- Mutations ---- */

  const submitCreateRole = async () => {
    if (!roleForm.code.trim() || !roleForm.libelle.trim()) {
      setError('Le code et le libellé sont obligatoires.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        code: roleForm.code.trim(),
        libelle: roleForm.libelle.trim(),
      };
      if (roleForm.description.trim()) payload.description = roleForm.description.trim();
      await rbac.creerRole(payload);
      flash('Rôle créé.');
      setShowCreateRole(false);
      setRoleForm({ code: '', libelle: '', description: '' });
      await fetchRoles();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const submitCreatePerm = async () => {
    if (!permForm.code.trim() || !permForm.libelle.trim()) {
      setError('Le code et le libellé sont obligatoires.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        code: permForm.code.trim(),
        libelle: permForm.libelle.trim(),
      };
      if (permForm.domaine.trim()) payload.domaine = permForm.domaine.trim();
      await rbac.creerPermission(payload);
      flash('Permission créée.');
      setShowCreatePerm(false);
      setPermForm({ code: '', libelle: '', domaine: '' });
      await fetchPermissions();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    setError(null);
    try {
      const c = confirm;
      if (c.type === 'attribuer') {
        await rbac.attribuerRole(c.adminId, Number(c.roleId));
        flash('Rôle attribué.');
        await fetchAdminRoles(c.adminId);
        await fetchEffectives(c.adminId);
      } else if (c.type === 'attribuer-temp') {
        const iso = toApiDateExpiration(c.dateExpiration);
        await rbac.attribuerRoleTemporaire(c.adminId, Number(c.roleId), iso);
        flash('Rôle temporaire attribué.');
        await fetchAdminRoles(c.adminId);
        await fetchEffectives(c.adminId);
      } else if (c.type === 'prolonger') {
        const iso = toApiDateExpiration(c.dateExpiration);
        await rbac.prolongerRole(c.adminId, Number(c.roleId), iso);
        flash('Attribution temporaire prolongée.');
        await fetchAdminRoles(c.adminId);
        await fetchEffectives(c.adminId);
      } else if (c.type === 'revoquer') {
        await rbac.revoquerRole(c.adminId, c.roleId);
        flash('Rôle révoqué.');
        await fetchAdminRoles(c.adminId);
        await fetchEffectives(c.adminId);
      } else if (c.type === 'attacher') {
        await rbac.attacherPermission(c.roleId, Number(c.permissionId));
        flash('Permission attachée.');
        await loadRolePerms(c.roleId, true);
      } else if (c.type === 'detacher') {
        await rbac.detacherPermission(c.roleId, c.permissionId);
        flash('Permission détachée.');
        await loadRolePerms(c.roleId, true);
      }
      setConfirm(null);
      setTempDate('');
      setAssignRoleId('');
      setAttachPermId('');
    } catch (err) {
      fail(err);
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const tabBtn = (id, label) => (
    <button
      type="button"
      onClick={() => setSubTab(id)}
      className={`px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-colors ${
        subTab === id
          ? 'bg-indigo-600 text-white border-indigo-600'
          : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-rose-800 text-xs font-bold flex justify-between gap-2">
          <span className="flex items-center gap-2">
            <ShieldAlert size={16} /> {error}
          </span>
          <button type="button" className="underline text-[11px]" onClick={() => setError(null)}>
            Fermer
          </button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-800 text-xs font-bold flex justify-between gap-2">
          <span className="flex items-center gap-2">
            <CheckCircle2 size={16} /> {success}
          </span>
          <button type="button" className="underline text-[11px]" onClick={() => setSuccess(null)}>
            Fermer
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {tabBtn('roles', 'Rôles')}
        {tabBtn('permissions', 'Permissions')}
        {tabBtn('attributions', 'Attributions')}
      </div>

      {/* ========== RÔLES ========== */}
      {subTab === 'roles' && (
        <div className="bg-white rounded-3xl border border-slate-200/80 overflow-hidden shadow-2xs">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <ShieldCheck size={16} className="text-indigo-600" /> Catalogue des rôles
            </h3>
            <div className="flex gap-2">
              <button type="button" onClick={fetchRoles} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <RefreshCw size={14} className={loadingRoles ? 'animate-spin' : ''} />
              </button>
              {superAdmin && (
                <button type="button" className="dsi-btn-primary text-xs" onClick={() => setShowCreateRole(true)}>
                  <Plus size={14} /> Créer un rôle
                </button>
              )}
            </div>
          </div>

          {loadingRoles ? (
            <div className="p-8 text-center text-xs text-slate-500">Chargement des rôles…</div>
          ) : roles.length === 0 ? (
            <div className="p-10 text-center space-y-2">
              <h4 className="text-sm font-bold text-slate-900">Aucun rôle configuré</h4>
              <p className="text-xs text-slate-500">Créez un rôle pour commencer à structurer les accès.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {roles.map((role) => (
                <div key={role.id}>
                  <button
                    type="button"
                    onClick={() => toggleRole(role.id)}
                    className="w-full flex items-center justify-between px-6 py-3 hover:bg-slate-50/70 text-left"
                  >
                    <div>
                      <div className="text-sm font-bold text-slate-900">{role.libelle}</div>
                      <div className="text-[11px] text-slate-500 font-mono">
                        {role.code}
                        {role.description ? ` — ${role.description}` : ''}
                      </div>
                      {role.created_at && (
                        <div className="text-[10px] text-slate-400 mt-0.5">Créé le {formatDate(role.created_at)}</div>
                      )}
                    </div>
                    {expandedRoleId === role.id ? (
                      <ChevronDown size={16} className="text-slate-400" />
                    ) : (
                      <ChevronRight size={16} className="text-slate-400" />
                    )}
                  </button>
                  {expandedRoleId === role.id && (
                    <div className="px-6 pb-4 pt-1 bg-slate-50/50 space-y-3">
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Permissions du rôle</p>
                      {!rolePermissions[role.id] ? (
                        <p className="text-xs text-slate-400">Chargement…</p>
                      ) : rolePermissions[role.id].length === 0 ? (
                        <p className="text-xs text-slate-400">Aucune permission attachée.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {rolePermissions[role.id].map((p) => (
                            <span
                              key={p.id}
                              className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-[11px] font-bold bg-white border border-slate-200 text-slate-600"
                            >
                              <span>
                                {p.libelle}
                                {p.domaine ? ` · ${p.domaine}` : ''}
                              </span>
                              {superAdmin && (
                                <button
                                  type="button"
                                  className="p-0.5 rounded-full hover:bg-rose-50 text-slate-400 hover:text-rose-600"
                                  title="Détacher"
                                  onClick={() =>
                                    setConfirm({
                                      type: 'detacher',
                                      roleId: role.id,
                                      permissionId: p.id,
                                      message: `Détacher « ${p.libelle} » de ce rôle ?`,
                                    })
                                  }
                                >
                                  <X size={11} />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                      {superAdmin && (
                        <div className="flex flex-wrap gap-2 items-end pt-1">
                          <select
                            className="dsi-input text-xs max-w-xs"
                            value={attachPermId}
                            onChange={(e) => setAttachPermId(e.target.value)}
                          >
                            <option value="">Attacher une permission…</option>
                            {permissions.length === 0 && (
                              <option value="" disabled>
                                (Charger l’onglet Permissions si la liste est vide)
                              </option>
                            )}
                            {permissions.map((p) => (
                              <option key={p.id} value={String(p.id)}>
                                {p.libelle} ({p.code})
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="dsi-btn-secondary text-xs"
                            disabled={!attachPermId}
                            onClick={() => {
                              if (!permissions.length) fetchPermissions();
                              setConfirm({
                                type: 'attacher',
                                roleId: role.id,
                                permissionId: attachPermId,
                                message: 'Attacher cette permission au rôle ?',
                              });
                            }}
                          >
                            Attacher
                          </button>
                          {!permissions.length && (
                            <button type="button" className="text-[11px] text-indigo-600 underline" onClick={fetchPermissions}>
                              Charger les permissions
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== PERMISSIONS ========== */}
      {subTab === 'permissions' && (
        <div className="bg-white rounded-3xl border border-slate-200/80 overflow-hidden shadow-2xs">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <KeyRound size={16} className="text-indigo-600" /> Catalogue des permissions
            </h3>
            <div className="flex gap-2">
              <button type="button" onClick={fetchPermissions} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <RefreshCw size={14} className={loadingPerms ? 'animate-spin' : ''} />
              </button>
              {superAdmin && (
                <button type="button" className="dsi-btn-primary text-xs" onClick={() => setShowCreatePerm(true)}>
                  <Plus size={14} /> Créer une permission
                </button>
              )}
            </div>
          </div>
          {loadingPerms ? (
            <div className="p-8 text-center text-xs text-slate-500">Chargement…</div>
          ) : permissions.length === 0 ? (
            <div className="p-10 text-center text-xs text-slate-500">Aucune permission configurée.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3">Code</th>
                    <th className="px-4 py-3">Libellé</th>
                    <th className="px-4 py-3">Domaine</th>
                    <th className="px-4 py-3">Créée le</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {permissions.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2 font-mono text-[11px]">{p.code}</td>
                      <td className="px-4 py-2 text-xs font-semibold">{p.libelle}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{p.domaine || '—'}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{formatDate(p.created_at) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ========== ATTRIBUTIONS ========== */}
      {subTab === 'attributions' && (
        <div className="bg-white rounded-3xl border border-slate-200/80 overflow-hidden shadow-2xs">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
              <UserCog size={16} className="text-indigo-600" /> Rôles & permissions effectives
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              L’attribution, la prolongation et la révocation sont réservées au Super Admin. Le Worker reste autoritaire.
            </p>
          </div>
          <div className="divide-y divide-slate-100">
            {admins.map((admin) => (
              <div key={admin.id} className="px-6 py-4 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-sm font-bold text-slate-900">{admin.nom_complet || admin.email}</div>
                    <div className="text-[11px] text-slate-500">{admin.email}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openAdminPanel(admin.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl text-[11px] font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200"
                  >
                    {openAdminId === admin.id ? 'Masquer' : 'Gérer'}
                  </button>
                </div>

                {openAdminId === admin.id && (
                  <div className="space-y-4 p-3 bg-slate-50 rounded-2xl border border-slate-200">
                    {/* Rôles actifs */}
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase mb-2">Rôles actifs</p>
                      {adminRoles[admin.id] === 'loading' ? (
                        <p className="text-xs text-slate-400">Chargement…</p>
                      ) : !Array.isArray(adminRoles[admin.id]) || adminRoles[admin.id].length === 0 ? (
                        <p className="text-xs text-slate-400">Aucun rôle attribué.</p>
                      ) : (
                        <div className="space-y-2">
                          {adminRoles[admin.id].map((r) => (
                            <div
                              key={r.id}
                              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200"
                            >
                              <div>
                                <span className="text-xs font-bold text-slate-800">{r.libelle}</span>
                                <span className="ml-2 font-mono text-[10px] text-slate-400">{r.code}</span>
                                <div className="text-[10px] text-slate-500 mt-0.5">
                                  {r.type_attribution === 'temporaire' ? (
                                    <>Temporaire · expire le {formatDate(r.date_expiration) || '—'}</>
                                  ) : (
                                    <>Permanent · aucune expiration</>
                                  )}
                                </div>
                              </div>
                              {superAdmin && (
                                <div className="flex flex-wrap gap-1">
                                  {r.type_attribution === 'temporaire' && (
                                    <button
                                      type="button"
                                      className="dsi-btn-ghost text-[10px]"
                                      onClick={() => {
                                        setTempDate('');
                                        setConfirm({
                                          type: 'prolonger',
                                          adminId: admin.id,
                                          roleId: r.id,
                                          needsDate: true,
                                          message: `Prolonger l’attribution temporaire de « ${r.libelle} ». Choisissez une nouvelle date d’expiration future.`,
                                        });
                                      }}
                                    >
                                      Prolonger
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="dsi-btn-ghost text-[10px] text-rose-700"
                                    onClick={() =>
                                      setConfirm({
                                        type: 'revoquer',
                                        adminId: admin.id,
                                        roleId: r.id,
                                        message: `Révoquer le rôle « ${r.libelle} » pour cet administrateur ?`,
                                      })
                                    }
                                  >
                                    Révoquer
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {superAdmin && (
                      <div className="space-y-2">
                        <p className="text-[10px] font-bold text-slate-400 uppercase">Attribuer un rôle</p>
                        <div className="flex flex-wrap gap-2 items-end">
                          <select
                            className="dsi-input text-xs"
                            value={assignRoleId}
                            onChange={(e) => setAssignRoleId(e.target.value)}
                          >
                            <option value="">Choisir un rôle…</option>
                            {roles.map((role) => (
                              <option key={role.id} value={String(role.id)}>
                                {role.libelle}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="dsi-btn-secondary text-xs"
                            disabled={!assignRoleId}
                            onClick={() =>
                              setConfirm({
                                type: 'attribuer',
                                adminId: admin.id,
                                roleId: assignRoleId,
                                message:
                                  'Attribuer ce rôle de façon permanente ? Si une attribution existe déjà, ses paramètres seront remplacés.',
                              })
                            }
                          >
                            Permanent
                          </button>
                          <button
                            type="button"
                            className="dsi-btn-secondary text-xs"
                            disabled={!assignRoleId}
                            onClick={() => {
                              setTempDate('');
                              setConfirm({
                                type: 'attribuer-temp',
                                adminId: admin.id,
                                roleId: assignRoleId,
                                needsDate: true,
                                message:
                                  'Attribuer ce rôle temporairement ? Si une attribution existe déjà, elle sera remplacée. Date d’expiration future obligatoire.',
                              });
                            }}
                          >
                            Temporaire
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Effectives */}
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase mb-2">Permissions effectives</p>
                      {adminEffectives[admin.id] === 'loading' ? (
                        <p className="text-xs text-slate-400">Chargement…</p>
                      ) : !Array.isArray(adminEffectives[admin.id]) || adminEffectives[admin.id].length === 0 ? (
                        <p className="text-xs text-slate-400">Aucune permission effective.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {adminEffectives[admin.id].map((p) => (
                            <span
                              key={p.id || p.code}
                              className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-white border border-slate-200 text-slate-600"
                              title={p.code}
                            >
                              {p.libelle || p.code}
                              {p.domaine ? ` · ${p.domaine}` : ''}
                            </span>
                          ))}
                        </div>
                      )}
                      <button
                        type="button"
                        className="text-[11px] text-indigo-600 underline mt-2"
                        onClick={() => fetchEffectives(admin.id)}
                      >
                        Actualiser les permissions effectives
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {!admins.length && (
              <div className="p-8 text-center text-xs text-slate-500">Aucun administrateur chargé.</div>
            )}
          </div>
        </div>
      )}

      {/* Modal créer rôle */}
      {showCreateRole && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => !busy && setShowCreateRole(false)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4">
            <h3 className="font-bold">Créer un rôle</h3>
            <label className="block space-y-1 text-xs">
              <span className="font-semibold text-slate-600">Code</span>
              <input
                className="dsi-input font-mono uppercase"
                value={roleForm.code}
                onChange={(e) => setRoleForm({ ...roleForm, code: e.target.value })}
                placeholder="MODERATEUR_CONTENU"
              />
            </label>
            <label className="block space-y-1 text-xs">
              <span className="font-semibold text-slate-600">Libellé</span>
              <input
                className="dsi-input"
                value={roleForm.libelle}
                onChange={(e) => setRoleForm({ ...roleForm, libelle: e.target.value })}
              />
            </label>
            <label className="block space-y-1 text-xs">
              <span className="font-semibold text-slate-600">Description (optionnel)</span>
              <textarea
                className="dsi-input"
                rows={2}
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
              />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" disabled={busy} onClick={() => setShowCreateRole(false)}>
                Annuler
              </button>
              <button type="button" className="dsi-btn-primary text-xs" disabled={busy} onClick={submitCreateRole}>
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal créer permission */}
      {showCreatePerm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => !busy && setShowCreatePerm(false)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4">
            <h3 className="font-bold">Créer une permission</h3>
            <label className="block space-y-1 text-xs">
              <span className="font-semibold text-slate-600">Code</span>
              <input
                className="dsi-input font-mono uppercase"
                value={permForm.code}
                onChange={(e) => setPermForm({ ...permForm, code: e.target.value })}
                placeholder="ADMINISTRATEURS_VOIR"
              />
            </label>
            <label className="block space-y-1 text-xs">
              <span className="font-semibold text-slate-600">Libellé</span>
              <input
                className="dsi-input"
                value={permForm.libelle}
                onChange={(e) => setPermForm({ ...permForm, libelle: e.target.value })}
              />
            </label>
            <label className="block space-y-1 text-xs">
              <span className="font-semibold text-slate-600">Domaine (optionnel)</span>
              <input
                className="dsi-input"
                value={permForm.domaine}
                onChange={(e) => setPermForm({ ...permForm, domaine: e.target.value })}
                placeholder="administration"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" disabled={busy} onClick={() => setShowCreatePerm(false)}>
                Annuler
              </button>
              <button type="button" className="dsi-btn-primary text-xs" disabled={busy} onClick={submitCreatePerm}>
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm generic */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => !busy && setConfirm(null)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4">
            <h3 className="font-bold">Confirmation</h3>
            <p className="text-xs text-slate-600">{confirm.message}</p>
            {confirm.needsDate && (
              <label className="block space-y-1 text-xs">
                <span className="font-semibold text-slate-600">Date d’expiration</span>
                <input
                  type="datetime-local"
                  className="dsi-input"
                  value={tempDate}
                  onChange={(e) => setTempDate(e.target.value)}
                />
              </label>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" disabled={busy} onClick={() => setConfirm(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="dsi-btn-primary text-xs"
                disabled={busy || (confirm.needsDate && !tempDate)}
                onClick={() => {
                  if (confirm.needsDate) {
                    setConfirm({ ...confirm, dateExpiration: tempDate, needsDate: false });
                    // run on next tick after state — call directly with date
                    const c = { ...confirm, dateExpiration: tempDate };
                    setConfirm(null);
                    (async () => {
                      setBusy(true);
                      setError(null);
                      try {
                        const iso = toApiDateExpiration(c.dateExpiration);
                        if (c.type === 'attribuer-temp') {
                          await rbac.attribuerRoleTemporaire(c.adminId, Number(c.roleId), iso);
                          flash('Rôle temporaire attribué.');
                        } else if (c.type === 'prolonger') {
                          await rbac.prolongerRole(c.adminId, Number(c.roleId), iso);
                          flash('Attribution temporaire prolongée.');
                        }
                        await fetchAdminRoles(c.adminId);
                        await fetchEffectives(c.adminId);
                        setAssignRoleId('');
                        setTempDate('');
                      } catch (err) {
                        fail(err);
                      } finally {
                        setBusy(false);
                      }
                    })();
                    return;
                  }
                  runConfirm();
                }}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
